import React, { useState, useMemo, useCallback } from 'react';
import Header from '../components/layout/Header';
import { useBuFilter } from '../hooks/useBuMapping';
import KPICard from '../components/ui/KPICard';
import ErrorBanner from '../components/ui/ErrorBanner';
import RevenueChart from '../components/charts/RevenueChart';
import GBBEstimatesChart from '../components/charts/GBBEstimatesChart';
import JobStatusChart from '../components/charts/JobStatusChart';
import CapacityByBUChart from '../components/charts/CapacityByBUChart';
import { useRevenue } from '../hooks/useRevenue';
import { useJobs } from '../hooks/useJobs';
import { useLeads } from '../hooks/useLeads';
import { useOpportunities } from '../hooks/useOpportunities';
import { useBudget } from '../hooks/useForecast';
import { useTeamStats } from '../hooks/useTeamStats';
import { useEstimates } from '../hooks/useEstimates';
import { useDispatchBoard } from '../hooks/useDispatch';
import { formatCurrency, formatNumber, toLocalDateStr } from '../utils/formatters';
import ManagerFilter from '../components/ui/ManagerFilter';
import DateRangePicker from '../components/ui/DateRangePicker';
import SyncStatus from '../components/ui/SyncStatus';

// ── Date range helpers ─────────────────────────────────────────────────────
const RANGE_PRESETS = [
  { label: 'MTD',          days: 'mtd' },
  { label: 'Last Month',   days: 'last_month' },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
  { label: 'YTD',          days: 'ytd' },
  { label: '6 Months',     days: 180 },
  { label: '12 Months',    days: 365 },
];

const toDateStr = toLocalDateStr;

// Next N weekdays (Mon-Fri) starting today, as YYYY-MM-DD strings
function getNextBusinessDays(n) {
  const days = [];
  let cursor = new Date();
  while (days.length < n) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) days.push(toDateStr(cursor));
    cursor = new Date(cursor.getTime() + 86400000);
  }
  return days;
}

function getPresetRange(opt) {
  const now = new Date();
  let start, end;
  if (opt === 'mtd') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = now;
  } else if (opt === 'last_month') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end   = new Date(now.getFullYear(), now.getMonth(), 0);
  } else if (opt === 'ytd') {
    start = new Date(now.getFullYear(), 0, 1);
    end = now;
  } else {
    start = new Date();
    start.setDate(start.getDate() - opt);
    end = now;
  }
  return { start: toDateStr(start), end: toDateStr(end) };
}

// ── Available metrics/widgets ─────────────────────────────────────────────
const ALL_KPIS = [
  { id: 'open_opps', title: 'Open Pipeline', icon: '\uD83D\uDCCB', color: '#3b82f6',
    getValue: (d) => formatNumber(d.openEstimateCustomers),
    sub: (d) => `Better tier value: ${formatCurrency(d.betterTierValue)}`,
    note: 'Count = customers with open (unsold) estimates \u00B7 Value = total of Better-tier options only',
    source: 'estimates' },
  { id: 'total_revenue', title: 'Total Revenue', icon: '\uD83D\uDCB0', color: '#22c55e', getValue: (d) => formatCurrency(d.totalRevenue), sub: 'ServiceTitan', source: 'revenue' },
  { id: 'scheduled_jobs', title: 'Scheduled Jobs', icon: '\uD83D\uDCC5', color: '#f59e0b', getValue: (d) => formatNumber(d.scheduledJobs), sub: (d) => `${d.totalJobs} total jobs`, source: 'jobs' },
  { id: 'total_jobs', title: 'Total Jobs', icon: '\uD83D\uDD27', color: '#6366f1', getValue: (d) => formatNumber(d.totalJobs), sub: (d) => `${d.openJobs} open`, source: 'jobs' },
  { id: 'completed_jobs', title: 'Completed Jobs', icon: '\u2705', color: '#22c55e', getValue: (d) => formatNumber(d.completedJobs), sub: 'Completed', source: 'jobs' },
  { id: 'avg_ticket', title: 'Avg Ticket', icon: '\uD83C\uDFAB', color: '#8b5cf6', getValue: (d) => d.completedJobs > 0 ? formatCurrency(d.completedRevenue / d.completedJobs) : '$0', sub: 'Per completed job', source: 'jobs' },
  { id: 'pipeline_value', title: 'Pipeline Value', icon: '\uD83D\uDCCA', color: '#ec4899', getValue: (d) => formatCurrency(d.pipelineValue), sub: 'Open opportunities', source: 'opportunities' },
  { id: 'total_leads', title: 'Total Leads', icon: '\uD83D\uDCE7', color: '#06b6d4', getValue: (d) => formatNumber(d.totalLeads), sub: 'GHL contacts', source: 'leads' },
  { id: 'in_progress', title: 'In Progress', icon: '\u26A1', color: '#f97316', getValue: (d) => formatNumber(d.inProgressJobs), sub: 'Active jobs', source: 'jobs' },
  { id: 'close_rate', title: 'Close Rate', icon: '\uD83C\uDFAF', color: '#14b8a6', getValue: (d) => d.totalOpps > 0 ? Math.round(d.wonOpps / d.totalOpps * 100) + '%' : '—', sub: 'Won/Total', source: 'opportunities' },
];

const ALL_CHARTS = [
  { id: 'revenue_chart', title: 'Revenue by Month', type: 'area' },
  { id: 'pipeline_chart', title: 'Open Estimates by Tier (Good / Better / Best)', type: 'bar' },
  { id: 'job_status_chart', title: 'Job Status Breakdown', type: 'pie' },
  { id: 'lead_source_chart', title: 'Open Capacity by Business Unit (Next 3 Business Days)', type: 'bar' },
];

const DEFAULT_CONFIG = {
  kpis: ['open_opps', 'total_revenue', 'scheduled_jobs', 'total_jobs'],
  charts: ['revenue_chart', 'pipeline_chart', 'job_status_chart', 'lead_source_chart'],
};

function loadConfig() {
  try {
    const raw = localStorage.getItem('dashboard_config');
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_CONFIG;
}

function saveConfig(config) {
  localStorage.setItem('dashboard_config', JSON.stringify(config));
}

// ── Progress Bar ───────────────────────────────────────────────────────────
function ProgressBar({ label, actual, budget, color, format }) {
  const pct = budget > 0 ? Math.min(100, Math.round((actual / budget) * 100)) : 0;
  const display = format === 'currency' ? formatCurrency : formatNumber;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151' }}>{label}</span>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
          {display(actual)} / {display(budget)} ({pct}%)
        </span>
      </div>
      <div style={styles.progressBg}>
        <div style={{ ...styles.progressFill, width: `${pct}%`, background: pct >= 100 ? '#22c55e' : color }} />
      </div>
    </div>
  );
}

// ── Team Section ──────────────────────────────────────────────────────────
function TeamSection({ teamStats }) {
  const { hs, ono, loading } = teamStats;
  if (loading) return <div style={{ ...styles.chartCard, textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Loading team data...</div>;

  return (
    <div style={styles.chartCard}>
      <h3 style={styles.chartTitle}>Team Overview</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
        <div style={styles.teamBlock}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>{'\uD83D\uDEE1\uFE0F'}</span>
            <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>Health & Safety Compliance</h4>
          </div>
          <div style={styles.complianceRing}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${hs.compliancePct >= 80 ? '#22c55e' : hs.compliancePct >= 50 ? '#f59e0b' : '#ef4444'} ${hs.compliancePct * 3.6}deg, #f1f5f9 0deg)` }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1rem', fontWeight: '800', color: '#1e293b' }}>{hs.compliancePct}%</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>Completed</span><span style={{ ...styles.teamStatVal, color: '#22c55e' }}>{hs.completed}</span></div>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>Pending</span><span style={{ ...styles.teamStatVal, color: '#f59e0b' }}>{hs.pending}</span></div>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>Overdue</span><span style={{ ...styles.teamStatVal, color: '#ef4444' }}>{hs.overdue}</span></div>
            </div>
          </div>
        </div>
        <div style={styles.teamBlock}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>{'\uD83D\uDCCB'}</span>
            <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>1-on-1 Sessions</h4>
          </div>
          <div style={styles.complianceRing}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `conic-gradient(${ono.completionPct >= 80 ? '#6366f1' : ono.completionPct >= 50 ? '#f59e0b' : '#ef4444'} ${ono.completionPct * 3.6}deg, #f1f5f9 0deg)` }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '1rem', fontWeight: '800', color: '#1e293b' }}>{ono.completionPct}%</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>This Week</span><span style={{ ...styles.teamStatVal, color: '#6366f1' }}>{ono.weeklyCompleted}</span></div>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>YTD Completed</span><span style={{ ...styles.teamStatVal, color: '#22c55e' }}>{ono.ytdCompleted}</span></div>
              <div style={styles.teamStatRow}><span style={styles.teamStatLabel}>Employees w/ 1:1</span><span style={styles.teamStatVal}>{ono.employeesWithOno} / {ono.totalEmployees}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helpers to filter data by date range ──────────────────────────────────
function isInRange(dateStr, startStr, endStr) {
  if (!dateStr) return false;
  const d = dateStr.slice(0, 10);
  return d >= startStr && d <= endStr;
}
function getInvoiceDate(inv) { return inv.invoiceDate || inv.issueDate || inv.createdOn || inv.dateAdded || inv.createdAt || inv.date || ''; }
function getJobDate(job) { return job.createdOn || job.completedOn || job.modifiedOn || ''; }

// ── Edit Panel ──────────────────────────────────────────────────────────
function EditPanel({ config, onSave, onClose }) {
  const [kpis, setKpis] = useState([...config.kpis]);
  const [charts, setCharts] = useState([...config.charts]);

  const toggleKpi = (id) => setKpis(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
  const toggleChart = (id) => setCharts(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  return (
    <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', border: '2px solid #6366f1', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>Customize Dashboard</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => { onSave({ kpis, charts }); onClose(); }} style={{ padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>Save</button>
          <button onClick={onClose} style={{ padding: '0.4rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>Cancel</button>
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>KPI Cards</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
          {ALL_KPIS.map(k => (
            <button key={k.id} onClick={() => toggleKpi(k.id)} style={{
              padding: '0.35rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', border: '1px solid',
              background: kpis.includes(k.id) ? k.color + '20' : '#f8fafc',
              borderColor: kpis.includes(k.id) ? k.color : '#e2e8f0',
              color: kpis.includes(k.id) ? k.color : '#64748b',
            }}>
              {kpis.includes(k.id) ? '\u2611 ' : '\u2610 '}{k.title}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Charts</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
          {ALL_CHARTS.map(c => (
            <button key={c.id} onClick={() => toggleChart(c.id)} style={{
              padding: '0.35rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
              background: charts.includes(c.id) ? '#ede9fe' : '#f8fafc',
              border: `1px solid ${charts.includes(c.id) ? '#6366f1' : '#e2e8f0'}`,
              color: charts.includes(c.id) ? '#6366f1' : '#64748b',
            }}>
              {charts.includes(c.id) ? '\u2611 ' : '\u2610 '}{c.title} ({c.type})
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function Dashboard() {
  const [dateRange, setDateRange] = useState(() => getPresetRange('ytd'));
  const { filterJob, filterInvoice } = useBuFilter();
  const [config, setConfig] = useState(loadConfig);
  const [editing, setEditing] = useState(false);
  const [managerId, setManagerId] = useState(null);

  const handleSaveConfig = useCallback((newConfig) => {
    setConfig(newConfig);
    saveConfig(newConfig);
  }, []);

  const { start, end } = dateRange;

  // Load data with server-side date filtering (with optional manager filter for admins)
  const mgrOpts = useMemo(() => ({ managerId }), [managerId]);
  const allRevenue = useRevenue(start, end, mgrOpts);
  const allJobs = useJobs(start, end, mgrOpts);
  const leads = useLeads(start, end);
  const opportunities = useOpportunities(start, end);
  const { budget } = useBudget(new Date().getFullYear());
  const teamStats = useTeamStats(start, end);
  const estimates = useEstimates('', start, end);
  const nextBusinessDays = useMemo(() => getNextBusinessDays(3), []);
  const dispatchBoard = useDispatchBoard(nextBusinessDays[0], nextBusinessDays[nextBusinessDays.length - 1]);

  const revenueData = useMemo(() => allRevenue.data.filter(inv => inv.source !== 'ghl'), [allRevenue.data]);
  const buFilteredRevenue = useMemo(() => revenueData.filter(filterInvoice), [revenueData, filterInvoice]);
  const buFilteredJobs = useMemo(() => allJobs.data.filter(filterJob), [allJobs.data, filterJob]);
  const openEstimates = useMemo(() => estimates.data.filter(e => e.status !== 'Sold'), [estimates.data]);

  // All metric values
  const metrics = useMemo(() => ({
    totalRevenue: buFilteredRevenue.reduce((sum, inv) => sum + (inv.total || inv.amount || 0), 0),
    totalJobs: buFilteredJobs.length,
    openJobs: buFilteredJobs.filter(j => !['Completed', 'Canceled'].includes(j.status)).length,
    scheduledJobs: buFilteredJobs.filter(j => j.status === 'Scheduled' || j.jobStatus === 'Scheduled').length,
    completedJobs: buFilteredJobs.filter(j => j.status === 'Completed' || j.jobStatus === 'Completed').length,
    completedRevenue: buFilteredJobs.filter(j => j.status === 'Completed' || j.jobStatus === 'Completed').reduce((s, j) => s + (j.total || 0), 0),
    inProgressJobs: buFilteredJobs.filter(j => ['InProgress', 'Dispatched'].includes(j.status)).length,
    openOpps: opportunities.data.filter(o => o.status !== 'lost').length,
    wonOpps: opportunities.data.filter(o => o.status === 'won').length,
    totalOpps: opportunities.data.length,
    pipelineValue: opportunities.data.filter(o => o.status === 'open').reduce((s, o) => s + (o.monetaryValue || 0), 0),
    totalLeads: leads.data.length,
    openEstimateCustomers: new Set(openEstimates.map(e => e.customer_id)).size,
    betterTierValue: openEstimates.filter(e => e.tier === 'Better').reduce((s, e) => s + (e.total || 0), 0),
  }), [buFilteredRevenue, buFilteredJobs, opportunities.data, leads.data, openEstimates]);

  const anyError = leads.error || opportunities.error || allRevenue.error || allJobs.error;
  const hasBudget = budget && (budget.revenue_budget > 0 || budget.job_count_budget > 0);
  const ghlEmpty = !leads.loading && leads.data.length === 0 && !opportunities.loading && opportunities.data.length === 0;

  // Chart renderers
  const chartRenderers = {
    revenue_chart: <RevenueChart data={buFilteredRevenue} />,
    pipeline_chart: <GBBEstimatesChart data={estimates.data} />,
    job_status_chart: <JobStatusChart data={buFilteredJobs} />,
    lead_source_chart: <CapacityByBUChart capacity={dispatchBoard.capacity} targetDates={nextBusinessDays} />,
  };

  const activeKpis = ALL_KPIS.filter(k => config.kpis.includes(k.id));
  const activeCharts = ALL_CHARTS.filter(c => config.charts.includes(c.id));

  return (
    <div>
      <Header title="Dashboard" />
      <div style={styles.content}>
        <SyncStatus sinceDate={start} />
        {anyError && <ErrorBanner message="Some data sources failed to load. Showing available data." />}
        {ghlEmpty && !anyError && (
          <div style={styles.infoBanner}>
            <span style={{ fontSize: '1rem' }}>{'\u2139\uFE0F'}</span>
            <span>HighLevel data not available — configure GHL API Key and Location ID in <a href="/settings" style={{ color: '#3b82f6', fontWeight: '600' }}>Settings</a> to see leads &amp; opportunities.</span>
          </div>
        )}

        {/* Date Switcher + Edit Button */}
        <div style={styles.dateRow}>
          <DateRangePicker onChange={setDateRange} defaultPresetIdx={2} />
          <ManagerFilter value={managerId} onChange={setManagerId} style={{ marginLeft: '0.5rem' }} />
          <button onClick={() => setEditing(!editing)} style={{ ...styles.rangeBtn, marginLeft: '0.5rem', color: editing ? '#6366f1' : '#475569', borderColor: editing ? '#6366f1' : '#e2e8f0' }}>
            {editing ? '\u2716 Close' : '\u270F\uFE0F Edit'}
          </button>
        </div>

        {editing && <EditPanel config={config} onSave={handleSaveConfig} onClose={() => setEditing(false)} />}

        {/* Unsold Estimates Card */}
        {(estimates.totalValue > 0 || estimates.loading) && (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.75rem', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '1.5rem' }}>📋</span>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Unsold Estimates</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#b45309' }}>{estimates.loading ? '…' : formatCurrency(estimates.totalValue)}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#78350f' }}>
              {estimates.loading ? '' : `${estimates.total} estimate${estimates.total !== 1 ? 's' : ''} pending — mid-range value`}
            </div>
          </div>
        )}

        {/* KPI Row */}
        <div style={styles.kpiRow}>
          {activeKpis.map(k => {
            const loadingMap = { revenue: allRevenue.loading, jobs: allJobs.loading, opportunities: opportunities.loading, leads: leads.loading, estimates: estimates.loading };
            const sub = typeof k.sub === 'function' ? k.sub(metrics) : k.sub;
            return <KPICard key={k.id} title={k.title} value={k.getValue(metrics)} icon={k.icon} color={k.color} loading={loadingMap[k.source]} sub={sub} note={k.note} />;
          })}
        </div>

        {/* Budget Progress */}
        {hasBudget && (
          <div style={styles.budgetSection}>
            <h3 style={styles.chartTitle}>Budget Progress ({new Date().getFullYear()})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {budget.revenue_budget > 0 && <ProgressBar label="Revenue" actual={metrics.totalRevenue} budget={budget.revenue_budget} color="#22c55e" format="currency" />}
              {budget.job_count_budget > 0 && <ProgressBar label="Jobs" actual={metrics.totalJobs} budget={budget.job_count_budget} color="#f59e0b" format="number" />}
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: activeCharts.length >= 2 ? '1fr 1fr' : '1fr', gap: '1rem', marginBottom: '1rem' }}>
          {activeCharts.map(c => (
            <div key={c.id} style={styles.chartCard}>
              <h3 style={styles.chartTitle}>{c.title}</h3>
              {chartRenderers[c.id]}
            </div>
          ))}
        </div>

        <TeamSection teamStats={teamStats} />
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem' },
  infoBanner: {
    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem',
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem',
    fontSize: '0.8rem', color: '#1e40af', marginBottom: '1rem',
  },
  dateRow: {
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
    background: '#fff', padding: '0.75rem 1rem', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '1rem',
  },
  rangeBtn: {
    padding: '0.4rem 0.75rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: '#475569',
    transition: 'all 0.15s',
  },
  rangeBtnActive: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
  dateInput: {
    padding: '0.3rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.375rem',
    fontSize: '0.75rem', color: '#1e293b', outline: 'none', background: '#f8fafc',
  },
  kpiRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' },
  budgetSection: {
    background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '1rem',
  },
  progressBg: { height: '10px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: '999px', transition: 'width 0.4s ease' },
  chartCard: { background: '#fff', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '1rem' },
  chartTitle: { margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' },
  teamBlock: { background: '#f8fafc', borderRadius: '0.625rem', padding: '1rem', border: '1px solid #e2e8f0' },
  complianceRing: { display: 'flex', alignItems: 'center', gap: '1rem' },
  teamStatRow: { display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', borderBottom: '1px solid #f1f5f9' },
  teamStatLabel: { fontSize: '0.8rem', color: '#64748b' },
  teamStatVal: { fontSize: '0.8rem', fontWeight: '700', color: '#1e293b' },
};
